import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, Rewind, FastForward, Keyboard } from "@phosphor-icons/react";
import { API_BASE } from "@/lib/api";
import { isRTL } from "@/lib/languages";

function fmtTs(sec) {
  if (sec == null || Number.isNaN(sec)) return "00:00";
  if (sec < 0) sec = 0;
  const total = Math.round(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function fmtSrtTs(sec) {
  if (sec == null) return "00:00:00,000";
  const ms = Math.round(sec * 1000);
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const r = ms % 1000;
  const pad = (n, w = 2) => String(n).padStart(w, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)},${pad(r, 3)}`;
}

function Waveform({ peaks, segments, duration, current, onSeek, height = 54 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !peaks?.length) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = parent.clientWidth;
    const cssH = height;
    canvas.width = cssW * dpr;
    canvas.height = cssH * dpr;
    canvas.style.width = cssW + "px";
    canvas.style.height = cssH + "px";
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, cssW, cssH);

    // Build quick index: segments sorted by start; for each pixel find the peak value + if it overlaps a segment
    const n = peaks.length;
    const mid = cssH / 2;
    const maxAmp = mid - 2;
    for (let x = 0; x < cssW; x += 1) {
      const peakIdx = Math.floor((x / cssW) * n);
      const p = peaks[Math.min(n - 1, peakIdx)];
      const h = Math.max(1, p * maxAmp);
      const t = (x / cssW) * (duration || 0);
      // Is this column inside a segment (i.e., covered by transcribed speech)?
      const covered = (segments || []).some((s) => t >= s.start && t <= s.end);
      const played = t <= current;
      if (played) ctx.fillStyle = covered ? "#0a0a0a" : "#e63946";
      else ctx.fillStyle = covered ? "rgba(10,10,10,0.35)" : "rgba(230, 57, 70, 0.35)";
      ctx.fillRect(x, mid - h, 1, h * 2);
    }

    // playhead
    if (duration) {
      const px = (current / duration) * cssW;
      ctx.fillStyle = "#e63946";
      ctx.fillRect(px - 1, 0, 2, cssH);
    }
  }, [peaks, segments, duration, current, height]);

  const onClick = (e) => {
    if (!duration || !onSeek) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    onSeek((x / rect.width) * duration);
  };

  if (!peaks?.length) return null;
  return (
    <div
      className="relative cursor-pointer select-none"
      onClick={onClick}
      style={{ height }}
      data-testid="waveform-canvas-wrap"
    >
      <canvas ref={canvasRef} data-testid="waveform-canvas" />
    </div>
  );
}

/**
 * Audio player + synchronized SRT viewer with waveform + keyboard shortcuts.
 * Keys: Space play/pause, J/L seek ±3s, K pause, , / . micro-seek ±0.04s,
 *       [ / ] previous/next segment, ↑/↓ volume.
 */
export default function AudioPreview({ projectId, trackId, segments, language, dialect, label, testId, onTimeUpdate, seekRef, showSegments = true }) {
  const audioRef = useRef(null);
  const listRef = useRef(null);
  const rootRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  // Download progress for the initial blob fetch.
  // `bytes` is what's downloaded so far, `total` is Content-Length (or 0 if unknown).
  const [progress, setProgress] = useState({ bytes: 0, total: 0 });
  // True while the <audio> element itself is buffering/seeking after the blob
  // is ready — covers Chrome's "loadstart → canplay" gap.
  const [buffering, setBuffering] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [peaks, setPeaks] = useState([]);
  const [volume, setVolume] = useState(1);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const rtl = isRTL(language);

  // Fetch audio as blob with real download-progress reporting via the
  // ReadableStream from `fetch`. Falls back to a single buffered read if the
  // browser doesn't expose `Response.body`.
  useEffect(() => {
    if (!projectId) return undefined;
    let blobUrl;
    let cancelled = false;
    setProgress({ bytes: 0, total: 0 });
    setAudioUrl(null);
    setLoadErr(null);
    (async () => {
      try {
        const qs = trackId ? `?track_id=${encodeURIComponent(trackId)}` : "";
        const res = await fetch(`${API_BASE}/projects/${projectId}/audio${qs}`, { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const total = Number(res.headers.get("Content-Length") || 0);
        let bytes = 0;
        let blob;
        if (res.body && typeof res.body.getReader === "function") {
          const reader = res.body.getReader();
          const chunks = [];
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (cancelled) { try { reader.cancel(); } catch { /* ignore */ } return; }
            chunks.push(value);
            bytes += value.byteLength;
            setProgress({ bytes, total });
          }
          blob = new Blob(chunks, { type: res.headers.get("Content-Type") || "audio/mpeg" });
        } else {
          blob = await res.blob();
          setProgress({ bytes: blob.size, total: blob.size });
        }
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setAudioUrl(blobUrl);
      } catch (e) {
        if (!cancelled) setLoadErr(e.message || "Could not load audio");
      }
    })();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [projectId, trackId]);

  // Fetch waveform peaks
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const qs = trackId ? `?track_id=${encodeURIComponent(trackId)}` : "";
        const res = await fetch(`${API_BASE}/projects/${projectId}/waveform${qs}`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.peaks)) setPeaks(data.peaks);
        }
      } catch (err) { console.warn("[audio-preview] waveform load failed", err); }
    })();
  }, [projectId, trackId]);

  const activeIndex = useMemo(() => {
    if (!segments?.length) return -1;
    for (let i = 0; i < segments.length; i += 1) {
      const s = segments[i];
      if (current >= s.start && current <= s.end) return i;
    }
    let last = -1;
    for (let i = 0; i < segments.length; i += 1) {
      if (segments[i].start <= current) last = i;
      else break;
    }
    return last;
  }, [segments, current]);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-seg-idx="${activeIndex}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [activeIndex]);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play(); else a.pause();
  }, []);

  const seek = useCallback((time) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min(duration || time, time));
  }, [duration]);

  // Expose `seek` to parent components via `seekRef` so the SRTViewer rows
  // can request a seek from outside this component.
  useEffect(() => {
    if (seekRef) seekRef.current = seek;
  }, [seek, seekRef]);

  const skip = useCallback((delta) => {
    seek((audioRef.current?.currentTime || 0) + delta);
  }, [seek]);

  const changeRate = () => {
    const rates = [0.75, 1, 1.25, 1.5, 2];
    const next = rates[(rates.indexOf(rate) + 1) % rates.length];
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const goToSegment = useCallback((delta) => {
    if (!segments?.length) return;
    let idx = activeIndex;
    if (idx < 0) idx = 0;
    const next = Math.max(0, Math.min(segments.length - 1, idx + delta));
    seek(segments[next].start);
  }, [segments, activeIndex, seek]);

  const changeVolume = useCallback((delta) => {
    const next = Math.max(0, Math.min(1, volume + delta));
    setVolume(next);
    if (audioRef.current) audioRef.current.volume = next;
  }, [volume]);

  // Keyboard shortcuts — scoped to when the preview is hovered or focused
  useEffect(() => {
    const handler = (e) => {
      // Ignore if user is typing in an input/textarea
      const tag = (e.target?.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      // Ignore if no audio yet
      if (!audioUrl) return;

      switch (e.key) {
        case " ":
          e.preventDefault(); togglePlay(); break;
        case "k": case "K":
          e.preventDefault(); audioRef.current?.pause(); break;
        case "j": case "J":
          e.preventDefault(); skip(-3); break;
        case "l": case "L":
          e.preventDefault(); skip(3); break;
        case ",":
          e.preventDefault(); skip(-0.04); break;
        case ".":
          e.preventDefault(); skip(0.04); break;
        case "[":
          e.preventDefault(); goToSegment(-1); break;
        case "]":
          e.preventDefault(); goToSegment(1); break;
        case "ArrowUp":
          e.preventDefault(); changeVolume(0.05); break;
        case "ArrowDown":
          e.preventDefault(); changeVolume(-0.05); break;
        default: break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [audioUrl, togglePlay, skip, goToSegment, changeVolume]);

  if (!segments?.length) return null;

  return (
    <div className="hard-border bg-white mt-4" data-testid={testId || "audio-preview"} ref={rootRef}>
      <div className="flex items-center justify-between px-5 py-3 bg-[--paper] border-b border-[--line]">
        <div>
          <div className="overline text-[--muted]">LISTEN & VERIFY</div>
          <div className="font-mono text-xs text-[--muted]">{label || "Preview"}</div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowShortcuts((v) => !v)}
            data-testid="shortcuts-toggle"
            className="border border-[--ink] p-1.5 hover:bg-[--ink] hover:text-white transition-colors"
            aria-label="Keyboard shortcuts"
            title="Keyboard shortcuts"
          >
            <Keyboard weight="bold" size={14} />
          </button>
          <div className="font-mono text-xs text-[--muted]">
            {fmtTs(current)} / {fmtTs(duration)}
          </div>
        </div>
      </div>

      {showShortcuts && (
        <div className="px-5 py-3 bg-[--ink] text-white font-mono text-[11px] grid grid-cols-2 sm:grid-cols-4 gap-y-1 gap-x-4" data-testid="shortcuts-panel">
          <div><kbd className="px-1.5 py-0.5 bg-white/10 mr-1">Space</kbd> play/pause</div>
          <div><kbd className="px-1.5 py-0.5 bg-white/10 mr-1">K</kbd> pause</div>
          <div><kbd className="px-1.5 py-0.5 bg-white/10 mr-1">J</kbd> -3s</div>
          <div><kbd className="px-1.5 py-0.5 bg-white/10 mr-1">L</kbd> +3s</div>
          <div><kbd className="px-1.5 py-0.5 bg-white/10 mr-1">,</kbd> -40ms</div>
          <div><kbd className="px-1.5 py-0.5 bg-white/10 mr-1">.</kbd> +40ms</div>
          <div><kbd className="px-1.5 py-0.5 bg-white/10 mr-1">[</kbd> prev segment</div>
          <div><kbd className="px-1.5 py-0.5 bg-white/10 mr-1">]</kbd> next segment</div>
          <div><kbd className="px-1.5 py-0.5 bg-white/10 mr-1">↑</kbd> vol +5%</div>
          <div><kbd className="px-1.5 py-0.5 bg-white/10 mr-1">↓</kbd> vol -5%</div>
        </div>
      )}

      {loadErr ? (
        <div className="p-6 text-sm text-red-600 font-mono" data-testid="audio-load-error">{loadErr}</div>
      ) : !audioUrl ? (
        <AudioLoader bytes={progress.bytes} total={progress.total} />
      ) : (
        <>
          <audio
            ref={audioRef}
            src={audioUrl}
            preload="metadata"
            onLoadStart={() => setBuffering(true)}
            onWaiting={() => setBuffering(true)}
            onCanPlay={() => setBuffering(false)}
            onCanPlayThrough={() => setBuffering(false)}
            onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
            onTimeUpdate={(e) => {
              const t = e.currentTarget.currentTime || 0;
              setCurrent(t);
              if (onTimeUpdate) onTimeUpdate(t);
            }}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            className="hidden"
          />
          {buffering && (
            <div className="px-5 py-2 bg-[--paper] border-b border-[--line] flex items-center gap-2 text-[11px] font-mono text-[--muted]" data-testid="audio-buffering-indicator">
              <span className="inline-block w-3 h-3 border-2 border-[--ink] border-t-transparent rounded-full animate-spin" />
              Buffering…
            </div>
          )}

          {/* Waveform */}
          {peaks?.length > 0 && (
            <div className="px-5 py-3 bg-[#fafafa] border-b border-[--line]">
              <Waveform
                peaks={peaks}
                segments={segments}
                duration={duration}
                current={current}
                onSeek={seek}
              />
              <div className="mt-1 font-mono text-[10px] text-[--muted] flex items-center justify-between">
                <span>0:00</span>
                <span className="inline-flex items-center gap-3">
                  <span><span className="inline-block w-2 h-2 bg-[--ink] mr-1.5"></span>speech</span>
                  <span><span className="inline-block w-2 h-2 bg-[--accent] mr-1.5"></span>silence / untranscribed</span>
                </span>
                <span>{fmtTs(duration)}</span>
              </div>
            </div>
          )}

          {/* Transport */}
          <div className="p-5 border-b border-[--line]">
            <div className="flex items-center gap-3">
              <button onClick={() => skip(-10)} data-testid="audio-rewind-btn" className="border border-[--ink] p-2 hover:bg-[--ink] hover:text-white transition-colors" aria-label="Rewind 10 s">
                <Rewind weight="bold" size={16} />
              </button>
              <button onClick={togglePlay} data-testid="audio-play-toggle" className="bg-[--accent] text-white p-3 hover:bg-[--accent-hi] transition-colors" aria-label={playing ? "Pause" : "Play"}>
                {playing ? <Pause weight="fill" size={18} /> : <Play weight="fill" size={18} />}
              </button>
              <button onClick={() => skip(10)} data-testid="audio-forward-btn" className="border border-[--ink] p-2 hover:bg-[--ink] hover:text-white transition-colors" aria-label="Forward 10 s">
                <FastForward weight="bold" size={16} />
              </button>
              <input
                type="range" min={0} max={Math.max(duration, 0.1)} step={0.01}
                value={current}
                onChange={(e) => seek(parseFloat(e.target.value))}
                data-testid="audio-scrubber"
                className="flex-1 accent-[--ink] h-1"
              />
              <button onClick={changeRate} data-testid="audio-rate-btn" className="border border-[--ink] px-2 py-2 text-xs font-mono font-bold hover:bg-[--ink] hover:text-white transition-colors min-w-[50px]" aria-label="Playback speed">
                {rate}×
              </button>
            </div>
          </div>

          {/* Synced SRT list */}
          {showSegments && (
          <div ref={listRef} className="max-h-[360px] overflow-y-auto scroll-thin divide-y divide-[--line]">
            {segments.map((s, i) => {
              const active = i === activeIndex;
              return (
                <button
                  key={`${s.start}-${s.end}-${i}`}
                  type="button"
                  data-seg-idx={i}
                  data-testid="audio-preview-segment"
                  onClick={() => seek(s.start)}
                  className={`w-full text-left p-3 transition-colors ${active ? "bg-[--accent]/10 border-l-4 border-l-[--accent]" : "hover:bg-[--paper] border-l-4 border-l-transparent"}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-mono text-[10px] ${active ? "text-[--accent]" : "text-[--signal]"}`}>
                      {fmtSrtTs(s.start)} → {fmtSrtTs(s.end)}
                      {s.speaker && <span className="ml-2 px-1.5 py-0.5 border border-[--ink]/40">{s.speaker}</span>}
                    </span>
                    <span className="font-mono text-[10px] text-[--muted]">#{s.index ?? i + 1}</span>
                  </div>
                  <div dir={rtl ? "rtl" : "ltr"} className={`text-sm leading-snug ${rtl ? "font-arabic text-right" : ""} ${active ? "font-semibold" : ""}`}>
                    {s.text}
                  </div>
                </button>
              );
            })}
          </div>
          )}
          {showSegments && dialect && (
            <div className="px-5 py-2 bg-[--paper] border-t border-[--line] font-mono text-[10px] text-[--muted]">
              {dialect}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function _formatBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Initial loader shown while we download the audio blob from the backend. */
function AudioLoader({ bytes, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((bytes / total) * 100)) : 0;
  const knownSize = total > 0;
  return (
    <div className="p-6 flex items-center gap-4" data-testid="audio-loader" role="status" aria-live="polite">
      <span className="inline-block w-6 h-6 border-2 border-[--ink] border-t-transparent rounded-full animate-spin shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="overline text-[--muted] flex items-center justify-between gap-3">
          <span>Loading audio…</span>
          <span className="font-mono text-[10px] tabular-nums" data-testid="audio-loader-bytes">
            {knownSize
              ? `${_formatBytes(bytes)} / ${_formatBytes(total)} · ${pct}%`
              : bytes > 0
                ? _formatBytes(bytes)
                : "starting…"}
          </span>
        </div>
        <div className="h-[6px] mt-2 bg-[--paper] border border-[--line] relative overflow-hidden">
          {knownSize ? (
            <div
              className="h-full bg-[--accent] transition-[width] duration-200"
              style={{ width: `${pct}%` }}
              data-testid="audio-loader-progress-bar"
            />
          ) : (
            // Indeterminate stripes when Content-Length is missing.
            <div className="absolute inset-0 progress-fill" data-testid="audio-loader-indeterminate" />
          )}
        </div>
      </div>
    </div>
  );
}
